import React, { ChangeEvent, useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AuthorisedSignatory,
  AuthorisedTrader,
  clientDetailedAuthorisedSignatoryInitialData,
  clientDetailedAuthorisedTraderInitialData,
  clientDetailedDirectorInitialData,
  ClientDetailedDto,
  clientDetailedOwnerInitialData,
  Director,
  Owner,
} from 'api/Clients/Types/ClientDetailedDto'
import { UpdateCompanyDetailsDto } from 'api/Clients/Types/UpdateCompanyDetailsDto'
import { WorldCheckStatus } from 'api/Clients/Types/WorldCheckStatus'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import AddButton from 'components/AddButton/AddButton'
import { Modal } from 'components/CustomModal'
import { ReactDatePicker } from 'components/DatePicker/DatePicker'
import {
  CommonReactDatePickerWrapper,
  FormBaseAntSelectWrapper,
  FormBaseButtonActionWrapper,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseNavigationHeader,
  FormBaseRow,
  FormBaseSectionBlock,
} from 'components/Form/Form.styles'
import MoreButton from 'components/MoreButton/MoreButton'
import { Select } from 'components/Select'
import { successToast, ToastContext } from 'components/Toast/Toast'
import {
  Field,
  FieldProps,
  Form,
  FormikErrors,
  FormikProps,
  useFormikContext,
  withFormik,
} from 'formik'
import { useCountriesToCountryCodes } from 'hooks/useCountriesToCountryCodes'
import { ChevronDown, Info } from 'lucide-react'
import { useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'
import { TicketsApiClient } from 'utils/api/ticketsApi'
import { WorldCheckApiClient } from 'utils/api/worldCheckApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { hasPermission } from 'utils/permissionsValidator'

import { TableWrapper } from '../Client.styles'
import { ClientDetailsWorldCheckHistory } from '../ClientWorldCheck/ClientDetailsWorldCheckHistory'
import { ClientDetailsWorldCheckModal } from '../ClientWorldCheck/ClientDetailsWorldCheckModal'
import { AuthorisedSignatoryForm } from './AuthorisedSignatoryForm'
import { AuthorisedTraderForm } from './AuthorisedTrader'
import { DirectorForm } from './DirectorForm'
import { OwnerForm } from './OwnerForm'

type ClientDetailsCorporateProps = {
  data: ClientDetailedDto
  companyTypes: NameDto[]
  worldCheckStatuses: WorldCheckStatus[]
  permissions: PermissionDto[]
  isEdit: boolean

  setEdit(value: boolean): void

  onSubmit(values: UpdateCompanyDetailsDto): void
}

export const ClientDetailsCorporateFormUI: React.FC<
  ClientDetailsCorporateProps & FormikProps<ClientDetailsCorporateValues>
> = (props) => {
  const {
    data,
    handleSubmit,
    values,
    setFieldValue,
    companyTypes,
    worldCheckStatuses,
    permissions,
    resetForm,
    submitForm,
    isEdit,
    setEdit,
    errors,
  } = props

  const worldCheckApiClient = useApiClient(WorldCheckApiClient)
  const ticketsApiClient = useApiClient(TicketsApiClient)
  const corporateWorldCheckScreeningTypeId = 2

  const countries = useAppSelector((state) => state.identity.countries)
  const legalBasesTypes = useAppSelector((state) => state.clients.legalBasesTypes)
  const ownershipStructures = useAppSelector((state) => state.clients.ownershipStructures)

  const [auth] = useContext(AuthSessionContext)
  const dateFormatType = auth?.user.dateFormatType.name

  const isViewMode = !isEdit
  const setToast = useContext(ToastContext)

  const [worldCheckHistoryModal, setWorldCheckHistoryModal] = useState<boolean>(false)
  const [worldCheckModal, setWorldCheckModal] = useState<{
    visible: boolean
    enableOngoingScreening: boolean
  }>({
    visible: false,
    enableOngoingScreening: false,
  })

  const uniqueCountryCodes = useCountriesToCountryCodes(countries)

  const [worldCheckClientDetailsModal, setWorldCheckClientDetailsModal] = useState<{
    visible: boolean
    caseId: string | undefined
  }>({
    visible: false,
    caseId: undefined,
  })

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await handleSubmit()
    setEdit(false)
  }

  const handleLegalRepresentation = (event: ChangeEvent<HTMLSelectElement>) => {
    const id = Number.parseInt(event.target.value, 10)
    const legalRepresentation = legalBasesTypes.find((x) => x.id === id)
    if (legalRepresentation) {
      props.setFieldValue('companyDetails.legalRepresentationBasis', legalRepresentation)
    }
  }

  const handleOwnershipStructure = (event: ChangeEvent<HTMLSelectElement>) => {
    const id = Number.parseInt(event.target.value, 10)
    const ownershipStructure = ownershipStructures.find((x) => x.id === id)
    if (ownershipStructure) {
      props.setFieldValue('companyDetails.ownershipStructure', ownershipStructure)
    }
  }

  const handleCompanyType = (event: ChangeEvent<HTMLSelectElement>) => {
    const companyTypeId = Number.parseInt(event.target.value, 10)
    const companyType = companyTypes.find((x) => x.id === companyTypeId)
    if (companyType) {
      props.setFieldValue('companyDetails.companyType', companyType)
    }
  }

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const save = () => {
    submitForm()
  }

  const onWorldCheckResolve = () => {
    setToast(successToast('WorldCheck status updated.'))
  }

  const onWorldCheckFailure = () => {}

  const onWorldCheckNoRecordsFoundResolve = async () => {
    setWorldCheckModal({ visible: false, enableOngoingScreening: false })
  }

  const handleWorldCheckScreeningModelOpen = (caseId: string) => {
    setWorldCheckClientDetailsModal({ visible: true, caseId })
  }

  const handleWorldCheckScreeningModelClose = () => {
    setWorldCheckClientDetailsModal({ visible: false, caseId: undefined })
  }

  const renderMenu = () => (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        disabled={
          !hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening) &&
          !hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults)
        }
      >
        WorldCheck
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuItem
          disabled={!hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening)}
          onSelect={() => setWorldCheckModal({ visible: true, enableOngoingScreening: false })}
        >
          Screening
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening)}
          onSelect={() => setWorldCheckModal({ visible: true, enableOngoingScreening: true })}
        >
          Ongoing Screening
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults)}
          onSelect={() => setWorldCheckHistoryModal(true)}
        >
          History
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )

  return (
    <React.Fragment>
      {worldCheckModal.visible && (
        <Modal
          visible={worldCheckModal.visible}
          width={1400}
          overflow='auto'
          onClose={() => setWorldCheckModal({ visible: false, enableOngoingScreening: false })}
          draggable={false}
        >
          <ClientDetailsWorldCheckModal
            clientId={data.id}
            typeId={corporateWorldCheckScreeningTypeId}
            worldCheckApiClient={worldCheckApiClient}
            ticketsApiClient={ticketsApiClient}
            enableOngoingScreening={worldCheckModal.enableOngoingScreening}
            onResolve={() => onWorldCheckResolve()}
            onFailure={() => onWorldCheckFailure()}
            onWorldCheckNoRecordsFoundResolve={onWorldCheckNoRecordsFoundResolve}
          />
        </Modal>
      )}
      {worldCheckHistoryModal && (
        <Modal
          draggable
          noDragZone='table'
          visible={worldCheckHistoryModal}
          width={1200}
          overflow='auto'
          onClose={() => setWorldCheckHistoryModal(false)}
        >
          <ClientDetailsWorldCheckHistory
            clientId={data.id}
            typeId={corporateWorldCheckScreeningTypeId}
            worldCheckApiClient={worldCheckApiClient}
            onWorldCheckScreeningModelOpen={handleWorldCheckScreeningModelOpen}
          />
        </Modal>
      )}
      {worldCheckClientDetailsModal.visible && (
        <Modal
          draggable
          noDragZone='table'
          visible={worldCheckClientDetailsModal.visible}
          width={1400}
          overflow='auto'
          onClose={handleWorldCheckScreeningModelClose}
        >
          <ClientDetailsWorldCheckModal
            clientId={data.id}
            typeId={corporateWorldCheckScreeningTypeId}
            caseId={worldCheckClientDetailsModal.caseId}
            worldCheckApiClient={worldCheckApiClient}
            ticketsApiClient={ticketsApiClient}
            isFromHistory
            onWorldCheckNoRecordsFoundResolve={onWorldCheckNoRecordsFoundResolve}
            onResolve={() => onWorldCheckResolve()}
            onFailure={() => onWorldCheckFailure()}
          />
        </Modal>
      )}
      <FormBaseContainer>
        <Form onSubmit={handleSubmitForm}>
          <FormBaseNavigationHeader>
            <FormBaseButtonActionWrapper>
              <ButtonGroup>
                {isEdit ? (
                  <React.Fragment>
                    <Button
                      type='button'
                      variant='outline'
                      className='is-danger'
                      onClick={cancel}
                      data-test='cancel-button'
                    >
                      Cancel
                    </Button>
                    <Button type='button' onClick={save} data-test='save-button'>
                      Save
                    </Button>
                  </React.Fragment>
                ) : (
                  <Button
                    disabled={!hasPermission(permissions, PermissionsEnum.CanEditClient)}
                    onClick={() => setEdit(true)}
                    data-test='edit-button'
                  >
                    Edit
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        aria-label='More options'
                        className='more-button'
                      />
                    }
                  >
                    <ChevronDown />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
                </DropdownMenu>
              </ButtonGroup>
            </FormBaseButtonActionWrapper>
          </FormBaseNavigationHeader>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Main Information</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Name</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='companyDetails.name' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Registration date</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <CommonReactDatePickerWrapper>
                    <ReactDatePicker
                      showYearDropdown
                      dropdownMode='select'
                      isClearable={isEdit}
                      disabled={isViewMode}
                      wrapperClassName='react-date-picker'
                      dateFormat={dateFormatType}
                      selected={
                        values.companyDetails?.registrationDate
                          ? new Date(values.companyDetails.registrationDate)
                          : null
                      }
                      onChange={(value) => {
                        setFieldValue('companyDetails.registrationDate', value?.toISOString())
                      }}
                      customInput={
                        <input type='text' style={{ width: '100%' }} autoComplete='off' />
                      }
                    />
                  </CommonReactDatePickerWrapper>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Registration number</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='companyDetails.registrationNumber' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Company Type</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <select
                    value={
                      values.companyDetails?.companyType?.id
                        ? values.companyDetails.companyType.id
                        : ''
                    }
                    autoComplete='off'
                    onChange={handleCompanyType}
                    disabled={isViewMode}
                  >
                    <option key={0} value='' disabled>
                      Select Company Type
                    </option>
                    {(companyTypes || []).map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Ownership structure</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    name='companyDetails.ownershipStructure.id'
                    disabled={isViewMode}
                    component='select'
                    onChange={handleOwnershipStructure}
                  >
                    <option value=''></option>
                    {(ownershipStructures || []).map((x) => (
                      <option value={x.id} key={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </Field>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Legal representation</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    name='companyDetails.legalRepresentationBasis.id'
                    disabled={isViewMode}
                    component='select'
                    onChange={handleLegalRepresentation}
                  >
                    {legalBasesTypes.map((x) => (
                      <option value={x.id} key={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </Field>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>LEI</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='companyDetails.legalEntityIdentifier' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
              {errors.companyDetails?.legalEntityIdentifier && (
                <FormBaseErrorMessage>
                  {errors.companyDetails.legalEntityIdentifier}
                </FormBaseErrorMessage>
              )}
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Website</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='companyDetails.website' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Company Info Locked</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='isCompanyInfoLocked' type='checkbox' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>FIX API User</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='isFIXApiUser' type='checkbox' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Contact</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Phone number</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <FormBaseAntSelectWrapper>
                    <Select
                      placeholder='Country code'
                      value={values.companyDetails.phoneNumber.countryCode || undefined}
                      allowClear
                      showSearch
                      disabled={isViewMode}
                      onChange={(value: string) => {
                        setFieldValue('companyDetails', {
                          ...values.companyDetails,
                          phoneNumber: {
                            ...values.companyDetails.phoneNumber,
                            countryCode: value,
                          },
                        })
                      }}
                    >
                      {uniqueCountryCodes.map((ccn) => (
                        <option value={ccn} key={ccn}>
                          {ccn}
                        </option>
                      ))}
                    </Select>
                  </FormBaseAntSelectWrapper>
                  <Field
                    type='text'
                    name='companyDetails.phoneNumber.number'
                    disabled={isViewMode}
                  />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Email address</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='companyDetails.email.address' type='text' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Company Registration Address</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Street</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='companyDetails.address.street' type='text' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>City</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='companyDetails.address.city' type='text' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Country</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    component='select'
                    name='companyDetails.address.country.id'
                    disabled={isViewMode}
                  >
                    {countries.map((country) => (
                      <option value={country.id} key={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </Field>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>State</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='companyDetails.address.state' type='text' disabled={isViewMode} />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Postal Code</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    name='companyDetails.address.postalCode'
                    type='text'
                    disabled={isViewMode}
                  />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Company Physical Address</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Street</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    name='companyDetails.physicalAddress.street'
                    type='text'
                    disabled={isViewMode}
                  />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>City</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    name='companyDetails.physicalAddress.city'
                    type='text'
                    disabled={isViewMode}
                  />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Country</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    component='select'
                    name='companyDetails.physicalAddress.country.id'
                    disabled={isViewMode}
                  >
                    {countries.map((country) => (
                      <option value={country.id} key={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </Field>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>State</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    name='companyDetails.physicalAddress.state'
                    type='text'
                    disabled={isViewMode}
                  />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Postal Code</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    name='companyDetails.physicalAddress.postalCode'
                    type='text'
                    disabled={isViewMode}
                  />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Status</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>World Check Status</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field
                    name='companyDetails.worldCheckStatus.id'
                    component='select'
                    disabled={!isEdit}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      const { value } = e.target
                      const fieldValue = worldCheckStatuses.find(
                        (x) => x.id === parseInt(value, 10)
                      )
                      if (fieldValue) {
                        setFieldValue('worldCheckStatus', fieldValue)
                      } else {
                        setFieldValue('worldCheckStatus', null)
                      }
                    }}
                  >
                    <option key={1} value=''>
                      {' '}
                    </option>
                    {(worldCheckStatuses || []).map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </Field>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Shareholders</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <ShareholdersTable isEdit={isEdit} />
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Directors</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <DirectorsTable isEdit={isEdit} />
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Authorised Signatories</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <AuthorisedSignatoriesTable isEdit={isEdit} />
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Authorised Traders</FormBaseFieldLabel>
            <FormBaseFieldBlock>
              <AuthorisedTradersTable isEdit={isEdit} />
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>
        </Form>
      </FormBaseContainer>
    </React.Fragment>
  )
}

const ShareholdersTable = ({ isEdit }: { isEdit: boolean }) => {
  const { values, setFieldValue } = useFormikContext<ClientDetailsCorporateValues>()

  const [shareholderModal, setShareholderModal] = useState<Owner | undefined>()

  const handleChangeOwner = (owner: Owner) => {
    const owners = (values.companyDetails?.owners || [])
      .filter((x) => x.id !== owner.id)
      .concat(owner)
    setFieldValue('companyDetails.owners', owners)
    setShareholderModal(undefined)
  }

  const handleDeleteOwner = (owner: Owner) => {
    setFieldValue(
      'companyDetails.owners',
      values.companyDetails?.owners.filter((x) => x.id !== owner.id)
    )
    setShareholderModal(undefined)
  }

  return (
    <React.Fragment>
      {shareholderModal && (
        <Modal visible={!!shareholderModal} onClose={() => setShareholderModal(undefined)}>
          <OwnerForm data={shareholderModal} onChange={handleChangeOwner} />
        </Modal>
      )}
      <AddButton
        text='Add'
        disabled={!isEdit}
        onClick={() => setShareholderModal(clientDetailedOwnerInitialData())}
      />
      <FormBaseRow>
        <FormBaseFieldGroup>
          <TableWrapper>
            <thead>
              <tr>
                <th>Shareholder Number</th>
                <th>Name</th>
                <th>Street</th>
                <th>City</th>
                <th>State</th>
                <th>Postal Code</th>
                <th>Country</th>
                <th>DOB</th>
                <th>Ownership %</th>
                <th
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    justifyContent: 'center',
                  }}
                >
                  TIN / GSTN
                  <Tooltip key='left'>
                    <TooltipTrigger>
                      <Info size={14} />
                    </TooltipTrigger>
                    <TooltipContent side='left'>
                      <span>Tax Identification Number / Goods Services Tax Number</span>
                    </TooltipContent>
                  </Tooltip>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <Field name='companyDetails.owners'>
                {({ field }: FieldProps<Owner[]>) =>
                  field.value.map((x: Owner, index) => (
                    <tr key={x.id}>
                      <td>{index + 1}</td>
                      <td>{x.name}</td>
                      <td>{x.street}</td>
                      <td>{x.city}</td>
                      <td>{x.state}</td>
                      <td>{x.zipCode}</td>
                      <td>{x?.country?.name}</td>
                      <td>{x.birthday}</td>
                      <td>{x.ownership}</td>
                      <td>{x.taxIdentificationNumber}</td>
                      <td align='right'>
                        <MoreButton
                          disabled={!isEdit}
                          items={[
                            {
                              name: 'Edit',
                              onClick: () => setShareholderModal(x),
                            },
                            {
                              name: 'Delete',
                              onClick: () => handleDeleteOwner(x),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))
                }
              </Field>
            </tbody>
          </TableWrapper>
        </FormBaseFieldGroup>
      </FormBaseRow>
    </React.Fragment>
  )
}

const DirectorsTable = ({ isEdit }: { isEdit: boolean }) => {
  const { values, setFieldValue } = useFormikContext<ClientDetailsCorporateValues>()

  const [directorModal, setDirectorModal] = useState<Director | undefined>()

  const handleChangeDirector = (director: Director) => {
    const directors = (values.companyDetails?.directors || [])
      .filter((x) => x.id !== director.id)
      .concat(director)
    setFieldValue('companyDetails.directors', directors)
    setDirectorModal(undefined)
  }

  const handleDeleteDirector = (director: Director) => {
    setFieldValue(
      'companyDetails.directors',
      values.companyDetails?.directors.filter((x) => x.id !== director.id)
    )
    setDirectorModal(undefined)
  }

  return (
    <React.Fragment>
      {directorModal && (
        <Modal visible={!!directorModal} onClose={() => setDirectorModal(undefined)}>
          <DirectorForm data={directorModal} onChange={handleChangeDirector} />
        </Modal>
      )}
      <AddButton
        text='Add'
        disabled={!isEdit}
        onClick={() => setDirectorModal(clientDetailedDirectorInitialData())}
      />
      <FormBaseRow>
        <FormBaseFieldGroup>
          <TableWrapper>
            <thead>
              <tr>
                <th>Director Number</th>
                <th>Name</th>
                <th>Street</th>
                <th>City</th>
                <th>State</th>
                <th>Postal Code</th>
                <th>Country</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <Field name='companyDetails.directors'>
                {({ field }: FieldProps<Director[]>) =>
                  field.value.map((x: Director, index) => (
                    <tr key={x.id}>
                      <td>{index + 1}</td>
                      <td>{x.name}</td>
                      <td>{x.street}</td>
                      <td>{x.city}</td>
                      <td>{x.state}</td>
                      <td>{x.zipCode}</td>
                      <td>{x?.country?.name ?? 'N/A'}</td>
                      <td align='right'>
                        <MoreButton
                          disabled={!isEdit}
                          items={[
                            {
                              name: 'Edit',
                              onClick: () => setDirectorModal(x),
                            },
                            {
                              name: 'Delete',
                              onClick: () => handleDeleteDirector(x),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))
                }
              </Field>
            </tbody>
          </TableWrapper>
        </FormBaseFieldGroup>
      </FormBaseRow>
    </React.Fragment>
  )
}

const AuthorisedSignatoriesTable = ({ isEdit }: { isEdit: boolean }) => {
  const { values, setFieldValue } = useFormikContext<ClientDetailsCorporateValues>()

  const [authorisedSignatoryModal, setAuthorisedSignatoryModal] = useState<
    AuthorisedSignatory | undefined
  >()

  const handleChangeAuthorisedSignatory = (authorisedSignatory: AuthorisedSignatory) => {
    const authorisedSignatories = (values.companyDetails?.authorisedSignatories || [])
      .filter((x) => x.id !== authorisedSignatory.id)
      .concat(authorisedSignatory)
    setFieldValue('companyDetails.authorisedSignatories', authorisedSignatories)
    setAuthorisedSignatoryModal(undefined)
  }

  const handleDeleteAuthorisedSignatory = (authorisedSignatory: AuthorisedSignatory) => {
    setFieldValue(
      'companyDetails.authorisedSignatories',
      values.companyDetails?.authorisedSignatories.filter((x) => x.id !== authorisedSignatory.id)
    )
    setAuthorisedSignatoryModal(undefined)
  }

  return (
    <React.Fragment>
      {authorisedSignatoryModal && (
        <Modal
          visible={!!authorisedSignatoryModal}
          onClose={() => setAuthorisedSignatoryModal(undefined)}
        >
          <AuthorisedSignatoryForm
            data={authorisedSignatoryModal}
            onChange={handleChangeAuthorisedSignatory}
          />
        </Modal>
      )}
      <AddButton
        text='Add'
        disabled={!isEdit}
        onClick={() => setAuthorisedSignatoryModal(clientDetailedAuthorisedSignatoryInitialData())}
      />
      <FormBaseRow>
        <FormBaseFieldGroup>
          <TableWrapper>
            <thead>
              <tr>
                <th>Signatories Number</th>
                <th>Name</th>
                <th>Role in the Company</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <Field name='companyDetails.authorisedSignatories'>
                {({ field }: FieldProps<AuthorisedSignatory[]>) =>
                  field.value.map((x: AuthorisedSignatory, index) => (
                    <tr key={x.id}>
                      <td>{index + 1}</td>
                      <td>{x.name}</td>
                      <td>{x.role}</td>
                      <td align='right'>
                        <MoreButton
                          disabled={!isEdit}
                          items={[
                            {
                              name: 'Edit',
                              onClick: () => setAuthorisedSignatoryModal(x),
                            },
                            {
                              name: 'Delete',
                              onClick: () => handleDeleteAuthorisedSignatory(x),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))
                }
              </Field>
            </tbody>
          </TableWrapper>
        </FormBaseFieldGroup>
      </FormBaseRow>
    </React.Fragment>
  )
}

const AuthorisedTradersTable = ({ isEdit }: { isEdit: boolean }) => {
  const { values, setFieldValue } = useFormikContext<ClientDetailsCorporateValues>()

  const [authorisedTraderModal, setAuthorisedTraderModal] = useState<AuthorisedTrader | undefined>()

  const handleChangeAuthorisedTrader = (authorisedTrader: AuthorisedTrader) => {
    const authorisedTraders = (values.companyDetails?.authorisedTraders || [])
      .filter((x) => x.id !== authorisedTrader.id)
      .concat(authorisedTrader)
    setFieldValue('companyDetails.authorisedTraders', authorisedTraders)
    setAuthorisedTraderModal(undefined)
  }

  const handleDeleteAuthorisedTrader = (authorisedTrader: AuthorisedTrader) => {
    setFieldValue(
      'companyDetails.authorisedTraders',
      values.companyDetails?.authorisedTraders.filter((x) => x.id !== authorisedTrader.id)
    )
    setAuthorisedTraderModal(undefined)
  }

  return (
    <React.Fragment>
      {authorisedTraderModal && (
        <Modal
          visible={!!authorisedTraderModal}
          onClose={() => setAuthorisedTraderModal(undefined)}
        >
          <AuthorisedTraderForm
            data={authorisedTraderModal}
            onChange={handleChangeAuthorisedTrader}
          />
        </Modal>
      )}
      <AddButton
        text='Add'
        disabled={!isEdit}
        onClick={() => setAuthorisedTraderModal(clientDetailedAuthorisedTraderInitialData())}
      />
      <FormBaseRow>
        <FormBaseFieldGroup>
          <TableWrapper>
            <thead>
              <tr>
                <th>Trader Number</th>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <Field name='companyDetails.authorisedTraders'>
                {({ field }: FieldProps<AuthorisedTrader[]>) =>
                  field.value.map((x: AuthorisedTrader, index) => (
                    <tr key={x.id}>
                      <td>{index + 1}</td>
                      <td>{x.name}</td>
                      <td>{x.role}</td>
                      <td>{x.email}</td>
                      <td align='right'>
                        <MoreButton
                          disabled={!isEdit}
                          items={[
                            {
                              name: 'Edit',
                              onClick: () => setAuthorisedTraderModal(x),
                            },
                            {
                              name: 'Delete',
                              onClick: () => handleDeleteAuthorisedTrader(x),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))
                }
              </Field>
            </tbody>
          </TableWrapper>
        </FormBaseFieldGroup>
      </FormBaseRow>
    </React.Fragment>
  )
}

type ClientDetailsCorporateValues = ClientDetailedDto

export const ClientDetailsCorporateForm = withFormik<
  ClientDetailsCorporateProps,
  ClientDetailsCorporateValues
>({
  mapPropsToValues: (props) => {
    return props.data
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    setSubmitting(true)
    const companyPayload: UpdateCompanyDetailsDto = {
      id: values.id,
      companyDetails: values.companyDetails,
      updatedAt: values.updatedDate || new Date().toISOString(),
      worldCheckStatus: values.worldCheckStatus,
      isCompanyInfoLocked: values.isCompanyInfoLocked || false,
      isFixApiUser: values.isFIXApiUser || false,
    }
    await props.onSubmit(companyPayload)
    setSubmitting(false)
  },
  validate: (values) => {
    const errors: FormikErrors<ClientDetailsCorporateValues> = {}

    const legalEntityIdentifier = values?.companyDetails?.legalEntityIdentifier || ''
    if (legalEntityIdentifier && legalEntityIdentifier.length > 20) {
      errors.companyDetails = {
        legalEntityIdentifier: 'The LEI maximum length is 20 characters.',
      }
    }

    return errors
  },
  enableReinitialize: true,
})(ClientDetailsCorporateFormUI)
